import { CSSProperties, FC, memo, useRef, useState } from "react";
import clsx from "classnames";
import { useNavigate } from "react-router-dom";
import { Image } from "../../core/Image/Image";
import {
  PathFeaturedCardData,
  FeaturedCard,
  LinkType,
  LinkFeaturedCardData,
  GameFeaturedCardData,
  EonLinkingStatus,
} from "@onmo/graphql/graphql";
import { selectIsAuthenticated } from "@onmo/auth";
import { useVisibility } from "../../../hooks/useVisibility";
import { PageRoutes } from "../../../constants/routes";
import { AnalyticsGA4 } from "../../../models/analyticsGA4";
import { Icon } from "../../core/Icon/Icon";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import {
  checkBYOGGame,
  getGameMetadata,
  getGameRating,
  isEssentialGame,
  isNewGame,
} from "../../../models/game";
import { GameMediaPreview } from "../GameMediaPreview/GameMediaPreview";
import { getYtbVideoThumbnail } from "../../../models/media";
import { GAME_CARD_RATIO } from "../../../constants/gamePack";
import { isTV } from "../../../utils/platform";
import { PopupLinkSteam } from "../PopupLinkSteam";
import { useAppSelector } from "../../hooks";

export const FeaturedCardItem: FC<{
  featuredCard: FeaturedCard;
  isShowVideo?: boolean;
  defaultCardWidth?: number;
  classes: Record<string, string>;
  steamAccountStatus?: EonLinkingStatus;
  isHideRating?: boolean;
  sessionId?: string;
}> = memo(
  ({
    featuredCard,
    isShowVideo,
    defaultCardWidth,
    steamAccountStatus,
    isHideRating,
    sessionId,
    classes,
  }) => {
    const ref = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const [isOpenVideo, setIsOpenVideo] = useState(false);
    const [isOpenLink, setIsOpenLink] = useState(false);
    const [isLinked, setIsLinked] = useState(false);
    const visible = useVisibility(ref);
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const gameData = (featuredCard.data as GameFeaturedCardData).game;
    const linkVideo = (featuredCard?.data as LinkFeaturedCardData)?.redirectUrl;
    const isEssential = isEssentialGame(gameData);
    const isBYOG = checkBYOGGame(gameData);
    const { gameImages, rating, price } = getGameMetadata(gameData);

    const sizeClass = isShowVideo ? classes.cardSizeVideo : classes.cardSize;
    const overrideStyle: CSSProperties | undefined = defaultCardWidth
      ? {
          width: defaultCardWidth,
          minWidth: defaultCardWidth,
          height: defaultCardWidth * GAME_CARD_RATIO,
          minHeight: defaultCardWidth * GAME_CARD_RATIO,
        }
      : undefined;

    const handleClick = () => {
      if (gameData) {
        const getGameType = (category: string): "essentials" | "byog" => {
          const parsed: string[] = JSON.parse(category);
          return parsed.includes("BYOG") ? "byog" : "essentials";
        };
        AnalyticsGA4.gameSelect(
          getGameType(gameData.category),
          getGameType(gameData.category),
          gameData.title,
        );
        navigate(`${PageRoutes.Game}/${gameData.id}`, {
          state: {
            prevPath: window.location.pathname,
            isFromGameTab: true,
          },
        });
      } else if (featuredCard.linkType === LinkType.InternalLink) {
        const internalPath = (featuredCard?.data as PathFeaturedCardData)
          ?.internalPath;
        navigate(internalPath, {
          state: { prevPath: window.location.pathname, isFromGameTab: true },
        });
      } else {
        window.open(
          (featuredCard?.data as LinkFeaturedCardData)?.redirectUrl,
          "_blank",
        );
      }
    };

    const onCloseVideo = () => {
      setIsOpenVideo(false);
    };

    const onOpenVideo = () => {
      if (
        featuredCard?.metadata?.isCreateSteam &&
        steamAccountStatus === EonLinkingStatus.NotLinked
      ) {
        setIsOpenLink(true);
      } else if (
        featuredCard?.metadata?.isLinkSteam &&
        steamAccountStatus === EonLinkingStatus.NotLinked
      ) {
        setIsLinked(true);
        setIsOpenLink(true);
      } else {
        setIsOpenVideo(true);
      }
    };

    if (linkVideo) {
      const accountLink =
        featuredCard?.metadata?.isCreateSteam ||
        featuredCard?.metadata?.isLinkSteam;
      if (
        (steamAccountStatus === EonLinkingStatus.Linked ||
          !isAuthenticated ||
          (isAuthenticated && !steamAccountStatus)) &&
        accountLink
      )
        return null;
      return (
        <>
          <div
            className={clsx(classes.videoContainer, sizeClass)}
            data-gamepad={`game-card-${featuredCard.group}`}
            data-gamepad-id={`game-card-${featuredCard.group}-${
              gameData?.id ?? ""
            }`}
            data-selected-class={classes.selected}
            onClick={onOpenVideo}
          >
            <div className={classes.video}>
              <Image
                src={getYtbVideoThumbnail(linkVideo)}
                className={classes.mediaImage}
              />
            </div>
          </div>
          {isOpenVideo && (
            <GameMediaPreview mediaList={[linkVideo]} onClose={onCloseVideo} />
          )}
          <PopupLinkSteam
            isOpen={isOpenLink}
            onClose={() => setIsOpenLink(false)}
            link={linkVideo}
            isLinked={isLinked}
          />
        </>
      );
    }

    return (
      <div
        ref={ref}
        className={clsx(classes.contentImg, sizeClass)}
        style={overrideStyle}
        data-gamepad={`game-card-${sessionId || featuredCard.group}`}
        data-gamepad-id={`game-card-${featuredCard.group}-${
          gameData?.id ?? ""
        }`}
        data-selected-class={classes.selected}
        onClick={handleClick}
      >
        {visible ? (
          <>
            <Image
              src={gameImages?.small_aaa}
              src2x={gameImages?.small_aaa2x}
              src3x={gameImages?.small_aaa3x}
              className={clsx(classes.gameImg, isTV && "loading")}
            />
            {isBYOG && (
              <div className={clsx(classes.more, classes.steam)}>
                <div className={classes.info}>
                  <Icon name="icon-tag" size={20} className={classes.value} />
                  <Typography
                    variant={TypographyVariant.BodyB2Regular}
                    className={classes.value}
                  >
                    Steam
                  </Typography>
                </div>
              </div>
            )}
            {isEssential && rating && !isHideRating && (
              <div className={classes.more}>
                <div className={classes.info}>
                  <Icon
                    name="icon-bold-star"
                    size={20}
                    className={clsx(classes.star, classes.value)}
                  />
                  <Typography
                    variant={TypographyVariant.BodyB2Medium}
                    className={classes.value}
                  >
                    {getGameRating(rating ?? 0)}
                  </Typography>
                </div>
              </div>
            )}
            {isBYOG && price?.length && price[0]?.discount > 0 && (
              <div className={clsx(classes.more, classes.discountSec)}>
                <Typography
                  variant={TypographyVariant.BodyB2Medium}
                  className={clsx(classes.valueDiscount, classes.value)}
                >
                  {price[0]?.discount}% OFF
                </Typography>
              </div>
            )}
            {isNewGame(gameData) && (
              <div className={clsx(classes.more, classes.newSec)}>
                <Typography
                  variant={TypographyVariant.HeadingH4Bold}
                  className={classes.valueNew}
                >
                  NEW
                </Typography>
              </div>
            )}
          </>
        ) : (
          <div className={clsx(classes.gameImg, isTV && "loading")} />
        )}
      </div>
    );
  },
);
